import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { CourseOption } from '@/types'
import type {
    EnrollmentErrors,
    EnrollmentField,
    EnrollmentResponse,
    EnrollmentValues
} from '@/lib/enrollment'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import {
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    Clock,
    Loader2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ENROLLMENT_FIELDS, ENROLLMENT_LIMITS, validateEnrollment } from '@/lib/enrollment'
import { ButtonLink, buttonVariants } from '@/components/generals/button-link'

import { SCHEDULE_OPTIONS } from '@/constants/site'

interface EnrollFormProps {
    courses: Array<CourseOption>
}

type FormValues = EnrollmentValues & {
    website: string
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

const INITIAL_VALUES: FormValues = {
    full_name: '',
    email: '',
    phone: '',
    course: '',
    schedule: '',
    message: '',
    website: ''
}

const FIELD_IDS: Record<EnrollmentField, string> = {
    full_name: 'enroll-name',
    email: 'enroll-email',
    phone: 'enroll-phone',
    course: 'enroll-course',
    schedule: 'enroll-schedule',
    message: 'enroll-message'
}

const INPUT_CLASS = 'w-full rounded-xl border border-input bg-background px-4 text-base text-foreground transition-colors outline-none placeholder:text-muted-foreground/70 hover:border-foreground/25 focus:border-primary focus:ring-4 focus:ring-primary/15 aria-[invalid=true]:border-destructive aria-[invalid=true]:focus:ring-destructive/15'

interface FieldProps {
    id: string
    label: string
    required?: boolean
    optional?: boolean
    error?: string
    className?: string
    children: ReactNode
}

const Field: FC<FieldProps> = ({
    id,
    label,
    required = false,
    optional = false,
    error,
    className,
    children
}): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-medium'>
                <span>
                    {label}
                    {required && <span className='text-primary' aria-hidden='true'> *</span>}
                </span>

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        Optional
                    </span>
                )}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {error && (
                <p id={`${id}-error`} className='mt-1.5 text-sm text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export const EnrollForm: FC<EnrollFormProps> = ({ courses }): ReactNode => {
    const router = useRouter()
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<EnrollmentErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [submitted, setSubmitted] = useState<{ name: string, email: string } | null>(null)
    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    // Preselect the course from /enroll?course=<slug>, once the query string is available in the browser.
    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.course === 'string' ? router.query.course : ''

        if (requested && courses.some(course => course.slug === requested)) {
            setValues(current => current.course ? current : { ...current, course: requested })
        }
    }, [router.isReady, router.query.course, courses])

    useEffect(() => {
        if (status !== 'success') return

        successHeadingRef.current?.focus()
        successHeadingRef.current?.scrollIntoView({ block: 'center' })
    }, [status])

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field !== 'website' && errors[field]) {
            setErrors(current => ({ ...current, [field]: undefined }))
        }
    }

    const focusFirstError = (found: EnrollmentErrors): void => {
        const first = ENROLLMENT_FIELDS.find(field => found[field])

        if (first) document.getElementById(FIELD_IDS[first])?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const { errors: found } = validateEnrollment(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            setStatus('idle')
            focusFirstError(found)

            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/enroll', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => null) as EnrollmentResponse | null

            if (response.ok && data?.ok) {
                setSubmitted({ name: values.full_name.trim(), email: values.email.trim() })
                setStatus('success')

                return
            }

            if (data && !data.ok && data.errors) {
                setErrors(data.errors)
                focusFirstError(data.errors)
            }

            setServerMessage(data && !data.ok ? data.message : "Something went wrong. Please try again in a moment.")
            setStatus('error')
        }
        catch {
            setServerMessage("We could not reach the server. Check your internet connection and try again.")
            setStatus('error')
        }
    }

    const reset = (): void => {
        setValues(INITIAL_VALUES)
        setErrors({})
        setServerMessage('')
        setSubmitted(null)
        setStatus('idle')
    }

    if (status === 'success' && submitted) {
        const firstName = submitted.name.split(' ')[0] || submitted.name

        return (
            <div role='status' className='rounded-3xl border bg-card p-8 text-center shadow-xl shadow-foreground/5 sm:p-12'>
                <span className='mx-auto flex size-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground'>
                    <CheckCircle2 className='size-7' aria-hidden='true' />
                </span>

                <h2 ref={successHeadingRef} tabIndex={-1} className='mt-6 text-2xl font-semibold outline-none sm:text-3xl'>
                    Thanks, {firstName}. Your request is in.
                </h2>

                <p className='mx-auto mt-3 max-w-md leading-relaxed text-muted-foreground'>
                    An advisor will contact you at <span className='font-medium break-all text-foreground'>{submitted.email}</span> within one working day to confirm your course, class time and fee.
                </p>

                <div className='mt-8 flex flex-col justify-center gap-3 sm:flex-row'>
                    <ButtonLink href='/courses' variant='outline'>
                        Explore more courses
                    </ButtonLink>

                    <button type='button' onClick={reset} className={buttonVariants({ variant: 'ghost' })}>
                        Send another request
                    </button>
                </div>
            </div>
        )
    }

    const describedBy = (field: EnrollmentField): string | undefined => errors[field] ? `${FIELD_IDS[field]}-error` : undefined

    return (
        <form
            noValidate
            onSubmit={handleSubmit}
            aria-labelledby='enroll-form-title'
            className='relative rounded-3xl border bg-card p-6 shadow-xl shadow-foreground/5 sm:p-8'
        >
            <div className='flex items-center justify-between gap-4 border-b pb-5'>
                <h2 id='enroll-form-title' className='text-xl font-semibold'>
                    Enrollment request
                </h2>

                <span className='inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground'>
                    <Clock className='size-3.5' aria-hidden='true' />
                    Takes 2 minutes
                </span>
            </div>

            <div className='mt-6 grid gap-5 sm:grid-cols-2'>
                <Field id={FIELD_IDS.full_name} label="Full name" required error={errors.full_name}>
                    <input
                        id={FIELD_IDS.full_name}
                        name='full_name'
                        type='text'
                        autoComplete='name'
                        required
                        maxLength={ENROLLMENT_LIMITS.name}
                        value={values.full_name}
                        onChange={update('full_name')}
                        aria-invalid={Boolean(errors.full_name)}
                        aria-describedby={describedBy('full_name')}
                        placeholder="Jane Smith"
                        className={cn(INPUT_CLASS, 'h-12')}
                    />
                </Field>

                <Field id={FIELD_IDS.email} label="Email" required error={errors.email}>
                    <input
                        id={FIELD_IDS.email}
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        required
                        maxLength={ENROLLMENT_LIMITS.email}
                        value={values.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('email')}
                        placeholder="you@example.com"
                        className={cn(INPUT_CLASS, 'h-12')}
                    />
                </Field>

                <Field id={FIELD_IDS.phone} label="Phone" optional error={errors.phone}>
                    <input
                        id={FIELD_IDS.phone}
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={ENROLLMENT_LIMITS.phone}
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone')}
                        placeholder="For a quick call back"
                        className={cn(INPUT_CLASS, 'h-12')}
                    />
                </Field>

                <Field id={FIELD_IDS.course} label="Course" error={errors.course}>
                    <div className='relative'>
                        <select
                            id={FIELD_IDS.course}
                            name='course'
                            value={values.course}
                            onChange={update('course')}
                            aria-invalid={Boolean(errors.course)}
                            aria-describedby={describedBy('course')}
                            className={cn(INPUT_CLASS, 'h-12 cursor-pointer appearance-none pr-11')}
                        >
                            <option value=''>Help me choose a course</option>

                            {courses.map(course => (
                                <option key={course.id} value={course.slug} data-mdlfy-cms={`courses:${course.id}:title`}>
                                    {course.title}
                                </option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                    </div>
                </Field>

                <fieldset className='sm:col-span-2' aria-describedby={describedBy('schedule')}>
                    <legend className='flex w-full items-baseline justify-between gap-3 text-sm font-medium'>
                        <span>Preferred class time</span>
                        <span className='text-xs font-normal text-muted-foreground'>Optional</span>
                    </legend>

                    <div className='mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4'>
                        {SCHEDULE_OPTIONS.map((option, index) => (
                            <label
                                key={option}
                                className='flex min-h-12 cursor-pointer items-center justify-center rounded-xl border border-input bg-background px-3 py-2 text-center text-sm font-medium transition-colors hover:border-foreground/25 has-[:checked]:border-primary has-[:checked]:bg-primary/5 has-[:checked]:text-primary has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-primary/15'
                            >
                                <input
                                    id={index === 0 ? FIELD_IDS.schedule : undefined}
                                    type='radio'
                                    name='schedule'
                                    value={option}
                                    checked={values.schedule === option}
                                    onChange={update('schedule')}
                                    className='sr-only'
                                />
                                {option}
                            </label>
                        ))}
                    </div>

                    {errors.schedule && (
                        <p id={`${FIELD_IDS.schedule}-error`} className='mt-1.5 text-sm text-destructive'>
                            {errors.schedule}
                        </p>
                    )}
                </fieldset>

                <Field id={FIELD_IDS.message} label="Message" optional error={errors.message} className='sm:col-span-2'>
                    <textarea
                        id={FIELD_IDS.message}
                        name='message'
                        rows={4}
                        maxLength={ENROLLMENT_LIMITS.message}
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={describedBy('message')}
                        placeholder="Tell us about your experience, your goals or any questions you have."
                        className={cn(INPUT_CLASS, 'min-h-28 resize-y py-3')}
                    />
                </Field>
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='enroll-website'>Website</label>
                <input
                    id='enroll-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='mt-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive'>
                    {serverMessage}
                </div>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className={buttonVariants({ size: 'lg', className: 'mt-7 w-full' })}
            >
                {status === 'submitting'
                    ? (
                        <>
                            <Loader2 className='animate-spin' aria-hidden='true' />
                            Sending your request...
                        </>
                    )
                    : (
                        <>
                            Send enrollment request
                            <ArrowRight aria-hidden='true' />
                        </>
                    )}
            </button>

            <p className='mt-3 text-center text-xs leading-relaxed text-muted-foreground'>
                Free and with no commitment. We only use your details to contact you about your enrollment.
            </p>
        </form>
    )
}
